import Modal from '../../Unknown/Modal'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { Box, Button, TextField } from '@mui/material'
import translate from '../../../i18n/translate'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import { FIELDS, useValidationSchema } from './useValidationSchema'
import { organizerApi } from '../../../modules/services/organizerService'

type OrganizerSettingsModalProps = {
  organizerInfo: {
    userId: number
    id: number
    earlyWithdrawalAnswerMessage?: string | null
    earlyWithdrawalDays: number
    earlyWithdrawalFee: number
    earlyWithdrawalPercent: number
    earlyWithdrawalRequestDate: Date | null
    earlyWithdrawalRequestMessage?: string | null
    freeTransactionCount: number
    isEarlyWithdrawalEnabled: boolean
    isEarlyWithdrawalRequested: boolean
    fastWithdrawalFee: number
    withdrawalType: 'default' | 'days'
    withdrawalDays: number
    transactionFee: number
    transactionMinSum: number
    adminNote?: string | null;
  }
  handleUpdateSuccess: (
    userId: number, 
    data: {
      isEarlyWithdrawalEnabled: boolean;
      earlyWithdrawalFee?: number | null;
      earlyWithdrawalDays?: number | null;
      earlyWithdrawalPercent?: number | null;
      freeTransactionCount: number;
      transactionMinSum: number;
      fastWithdrawalFee: number;
      transactionFee: number;
      withdrawalType: string;
      withdrawalDays?: number | null;
      adminNote?: string | null;
    },
  ) => void;
}

export const WITHDRAWAL_TYPES = {
  DEFAULT: 'default',
  DAYS: 'days'
}

const DEFAULT_FREE_TRANSACTION_COUNT = 2
const DEFAULT_TRANSACTION_FEE = 20
const DEFAULT_FAST_WITHDRAWAL_FEE = 25
const DEFAULT_TRANSACTION_MIN_SUM = 5000

export default function OrganizerSettingsModal({
  organizerInfo,
  handleUpdateSuccess
}: OrganizerSettingsModalProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { toggleOrganizerSettingsModal } = modalsSlice.actions
  const visible = useAppSelector(state => state.modalsReducer.organizerSettings)

  const [
    updateOrganizerSettings,
    { isLoading }
  ] = organizerApi.useUpdateOrganizerSettingsMutation()


  const handleSubmit = async (values: any) => {
    try {
      if (organizerInfo?.userId) {
        const data = {
          organizerId: organizerInfo?.id,
          isEarlyWithdrawalEnabled: values?.[FIELDS.IS_EARLY_WITHDRAWAL_ENABLED],
          earlyWithdrawalFee: values?.[FIELDS.IS_EARLY_WITHDRAWAL_ENABLED] ? Number((values?.[FIELDS.EARLY_WITHDRAWAL_FEE] / 100).toFixed(4)) : null,
          earlyWithdrawalDays: values?.[FIELDS.IS_EARLY_WITHDRAWAL_ENABLED] ? values?.[FIELDS.EARLY_WITHDRAWAL_DAYS] : null,
          earlyWithdrawalPercent: values?.[FIELDS.IS_EARLY_WITHDRAWAL_ENABLED] ? Number((values?.[FIELDS.EARLY_WITHDRAWAL_PERCENT] / 100).toFixed(4)) : null,
          freeTransactionCount: values?.[FIELDS.FREE_TRANSACTION_COUNT],
          transactionMinSum: values?.[FIELDS.TRANSACTION_MIN_SUM],
          transactionFee: values?.[FIELDS.TRANSACTION_FEE],
          fastWithdrawalFee: values?.[FIELDS.FAST_WITHDRAWAL_FEE],
          withdrawalType: values?.[FIELDS.WITHDRAWAL_TYPE],
          withdrawalDays: values?.[FIELDS.WITHDRAWAL_TYPE] === WITHDRAWAL_TYPES.DAYS ? values?.[FIELDS.WITHDRAWAL_DAYS] : null,
          adminNote: values?.[FIELDS.ADMIN_NOTE] || null,
        }

        await updateOrganizerSettings({
          userId: organizerInfo.userId,
          ...data,
        });

        handleUpdateSuccess(organizerInfo.userId, data);
      }
      dispatch(toggleOrganizerSettingsModal())
    } catch (error) {
      toast.error(translate('downloadReportError'))
    }
  }

  const onCloseModal = () => {
    dispatch(toggleOrganizerSettingsModal())
  }

  const validationSchema = useValidationSchema()

  return (
    <Modal
      title={translate('organizerSettingsModalTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '500px' }}
      setVisible={onCloseModal}
    >
      <Formik
        initialValues={{
          [FIELDS.FREE_TRANSACTION_COUNT]: organizerInfo?.freeTransactionCount !== null && organizerInfo?.freeTransactionCount !== undefined ? organizerInfo?.freeTransactionCount : DEFAULT_FREE_TRANSACTION_COUNT,
          [FIELDS.TRANSACTION_FEE]: organizerInfo?.transactionFee !== null && organizerInfo?.transactionFee !== undefined ? organizerInfo?.transactionFee : DEFAULT_TRANSACTION_FEE,
          [FIELDS.FAST_WITHDRAWAL_FEE]: organizerInfo?.fastWithdrawalFee !== null && organizerInfo?.fastWithdrawalFee !== undefined ? organizerInfo?.fastWithdrawalFee : DEFAULT_FAST_WITHDRAWAL_FEE,
          [FIELDS.TRANSACTION_MIN_SUM]: organizerInfo?.transactionMinSum !== null && organizerInfo?.transactionMinSum !== undefined ? organizerInfo?.transactionMinSum : DEFAULT_TRANSACTION_MIN_SUM,
          [FIELDS.IS_EARLY_WITHDRAWAL_ENABLED]: organizerInfo?.isEarlyWithdrawalEnabled || false,
          [FIELDS.EARLY_WITHDRAWAL_PERCENT]: organizerInfo?.earlyWithdrawalPercent !== null && organizerInfo?.earlyWithdrawalPercent !== undefined ? Number((organizerInfo.earlyWithdrawalPercent * 100).toFixed(2)) : null,
          [FIELDS.EARLY_WITHDRAWAL_DAYS]: organizerInfo?.earlyWithdrawalDays ?? null,
          [FIELDS.EARLY_WITHDRAWAL_FEE]: organizerInfo?.earlyWithdrawalFee !== null && organizerInfo?.earlyWithdrawalFee !== undefined ? Number((organizerInfo.earlyWithdrawalFee * 100).toFixed(2)) : null,
          [FIELDS.WITHDRAWAL_TYPE]: organizerInfo?.withdrawalType ?? WITHDRAWAL_TYPES.DEFAULT,
          [FIELDS.WITHDRAWAL_DAYS]: organizerInfo?.withdrawalType === WITHDRAWAL_TYPES.DAYS ? organizerInfo?.withdrawalDays ?? null : null,
          [FIELDS.ADMIN_NOTE]: organizerInfo?.adminNote || null,
        }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({ errors, values, touched, handleBlur, handleChange, setFieldValue, isValid }) => {
          return (
            <Form>
              <Box
                my={2}
                p={2}
                display='flex'
                flexDirection='column'
                justifyContent='center'
                width={1}
                alignItems='center'
                gap={2}
              >
                <TextField
                  name={FIELDS.FREE_TRANSACTION_COUNT}
                  value={values?.[FIELDS.FREE_TRANSACTION_COUNT]}
                  error={touched?.[FIELDS.FREE_TRANSACTION_COUNT] && Boolean(errors?.[FIELDS.FREE_TRANSACTION_COUNT])}
                  helperText={touched?.[FIELDS.FREE_TRANSACTION_COUNT] && errors?.[FIELDS.FREE_TRANSACTION_COUNT]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant='outlined'
                  disabled={isLoading}
                  fullWidth
                  type='number'
                  label={intl.formatMessage({
                    id: 'organizerFreeTransactionCount'
                  })}
                  placeholder={intl.formatMessage({
                    id: 'organizerFreeTransactionCount'
                  })}
                  dir='ltr'
                  sx={{
                    '.MuiInputBase-root.MuiInput-root:before': {
                      borderBottom: '0px'
                    },
                    '.MuiInputBase-root.MuiInput-root:hover': {
                      borderBottom: '0px'
                    }
                  }}
                />
                <TextField
                  name={FIELDS.TRANSACTION_FEE}
                  value={values?.[FIELDS.TRANSACTION_FEE]}
                  error={touched?.[FIELDS.TRANSACTION_FEE] && Boolean(errors?.[FIELDS.TRANSACTION_FEE])}
                  helperText={touched?.[FIELDS.TRANSACTION_FEE] && errors?.[FIELDS.TRANSACTION_FEE]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant='outlined'
                  disabled={isLoading}
                  fullWidth
                  type='number'
                  label={intl.formatMessage({ id: 'organizerTransactionFee' })}
                  placeholder={intl.formatMessage({
                    id: 'organizerTransactionFee'
                  })}
                  dir='ltr'
                  sx={{
                    '.MuiInputBase-root.MuiInput-root:before': {
                      borderBottom: '0px'
                    },
                    '.MuiInputBase-root.MuiInput-root:hover': {
                      borderBottom: '0px'
                    }
                  }}
                />
                <TextField
                  name={FIELDS.FAST_WITHDRAWAL_FEE}
                  value={values?.[FIELDS.FAST_WITHDRAWAL_FEE]}
                  error={touched?.[FIELDS.FAST_WITHDRAWAL_FEE] && Boolean(errors?.[FIELDS.FAST_WITHDRAWAL_FEE])}
                  helperText={touched?.[FIELDS.FAST_WITHDRAWAL_FEE] && errors?.[FIELDS.FAST_WITHDRAWAL_FEE]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant='outlined'
                  disabled={isLoading}
                  fullWidth
                  type='number'
                  label={intl.formatMessage({ id: 'organizerFastWithdrawalFee' })}
                  placeholder={intl.formatMessage({
                    id: 'organizerFastWithdrawalFee'
                  })}
                  dir='ltr'
                  sx={{
                    '.MuiInputBase-root.MuiInput-root:before': {
                      borderBottom: '0px'
                    },
                    '.MuiInputBase-root.MuiInput-root:hover': {
                      borderBottom: '0px'
                    }
                  }}
                />
                <TextField
                  name={FIELDS.TRANSACTION_MIN_SUM}
                  value={values?.[FIELDS.TRANSACTION_MIN_SUM]}
                  error={touched?.[FIELDS.TRANSACTION_MIN_SUM] && Boolean(errors?.[FIELDS.TRANSACTION_MIN_SUM])}
                  helperText={touched?.[FIELDS.TRANSACTION_MIN_SUM] && errors?.[FIELDS.TRANSACTION_MIN_SUM]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant='outlined'
                  disabled={isLoading}
                  fullWidth
                  type='number'
                  label={intl.formatMessage({ id: 'organizerTransactionMinSum' })}
                  placeholder={intl.formatMessage({
                    id: 'organizerTransactionMinSum'
                  })}
                  dir='ltr'
                  sx={{
                    '.MuiInputBase-root.MuiInput-root:before': {
                      borderBottom: '0px'
                    },
                    '.MuiInputBase-root.MuiInput-root:hover': {
                      borderBottom: '0px'
                    }
                  }}
                />
                <Button
                  sx={{
                    color: '#fc7f04',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start'
                  }}
                  startIcon={
                    values?.[FIELDS.IS_EARLY_WITHDRAWAL_ENABLED] ? (
                      <CheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )
                  }
                  onClick={() => setFieldValue(FIELDS.IS_EARLY_WITHDRAWAL_ENABLED, !values?.[FIELDS.IS_EARLY_WITHDRAWAL_ENABLED])}
                >
                  {translate('organizerEarlyWithdrawalEnabled')}
                </Button>
                {values?.[FIELDS.IS_EARLY_WITHDRAWAL_ENABLED] ? (
                  <Box
                    display='flex'
                    flexDirection='column'
                    justifyContent='center'
                    width={1}
                    alignItems='center'
                    gap={2}
                  >
                    <TextField
                      name={FIELDS.EARLY_WITHDRAWAL_PERCENT}
                      value={values?.[FIELDS.EARLY_WITHDRAWAL_PERCENT]}
                      error={touched?.[FIELDS.EARLY_WITHDRAWAL_PERCENT] && Boolean(errors?.[FIELDS.EARLY_WITHDRAWAL_PERCENT])}
                      helperText={touched?.[FIELDS.EARLY_WITHDRAWAL_PERCENT] && errors?.[FIELDS.EARLY_WITHDRAWAL_PERCENT]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant='outlined'
                      disabled={isLoading}
                      fullWidth
                      type='number'
                      label={intl.formatMessage({
                        id: 'organizerEarlyWithdrawalPercent'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'organizerEarlyWithdrawalPercent'
                      })}
                      dir='ltr'
                      sx={{
                        '.MuiInputBase-root.MuiInput-root:before': {
                          borderBottom: '0px'
                        },
                        '.MuiInputBase-root.MuiInput-root:hover': {
                          borderBottom: '0px'
                        }
                      }}
                    />
                    <TextField
                      name={FIELDS.EARLY_WITHDRAWAL_DAYS}
                      value={values?.[FIELDS.EARLY_WITHDRAWAL_DAYS]}
                      error={touched?.[FIELDS.EARLY_WITHDRAWAL_DAYS] && Boolean(errors?.[FIELDS.EARLY_WITHDRAWAL_DAYS])}
                      helperText={touched?.[FIELDS.EARLY_WITHDRAWAL_DAYS] && errors?.[FIELDS.EARLY_WITHDRAWAL_DAYS]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant='outlined'
                      disabled={isLoading}
                      fullWidth
                      type='number'
                      label={intl.formatMessage({
                        id: 'organizerEarlyWithdrawalDays'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'organizerEarlyWithdrawalDays'
                      })}
                      dir='ltr'
                      sx={{
                        '.MuiInputBase-root.MuiInput-root:before': {
                          borderBottom: '0px'
                        },
                        '.MuiInputBase-root.MuiInput-root:hover': {
                          borderBottom: '0px'
                        }
                      }}
                    />
                    <TextField
                      name={FIELDS.EARLY_WITHDRAWAL_FEE}
                      value={values?.[FIELDS.EARLY_WITHDRAWAL_FEE]}
                      error={touched?.[FIELDS.EARLY_WITHDRAWAL_FEE] && Boolean(errors?.[FIELDS.EARLY_WITHDRAWAL_FEE])}
                      helperText={touched?.[FIELDS.EARLY_WITHDRAWAL_FEE] && errors?.[FIELDS.EARLY_WITHDRAWAL_FEE]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      variant='outlined'
                      disabled={isLoading}
                      fullWidth
                      type='number'
                      label={intl.formatMessage({
                        id: 'organizerEarlyWithdrawalFee'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'organizerEarlyWithdrawalFee'
                      })}
                      dir='ltr'
                      sx={{
                        '.MuiInputBase-root.MuiInput-root:before': {
                          borderBottom: '0px'
                        },
                        '.MuiInputBase-root.MuiInput-root:hover': {
                          borderBottom: '0px'
                        }
                      }}
                    />
                  </Box>
                ) : null}
                <TextField
                  name={FIELDS.ADMIN_NOTE}
                  value={values?.[FIELDS.ADMIN_NOTE]}
                  error={touched?.[FIELDS.ADMIN_NOTE] && Boolean(errors?.[FIELDS.ADMIN_NOTE])}
                  helperText={touched?.[FIELDS.ADMIN_NOTE] && errors?.[FIELDS.ADMIN_NOTE]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant='outlined'
                  multiline
                  rows={3}
                  color='warning'
                  disabled={isLoading}
                  fullWidth
                  label={intl.formatMessage({
                    id: 'organizerSettingsAdminNote'
                  })}
                  placeholder={intl.formatMessage({
                    id: 'organizerSettingsAdminNote'
                  })}
                  dir='ltr'
                  sx={{
                    '.MuiInputBase-root.MuiInput-root:before': {
                      borderBottom: '0px'
                    },
                    '.MuiInputBase-root.MuiInput-root:hover': {
                      borderBottom: '0px'
                    }
                  }}
                />
                <Box display='flex' width={1}>
                  <Button
                    variant='contained'
                    color='warning'
                    fullWidth
                    onClick={() => setFieldValue(FIELDS.WITHDRAWAL_TYPE, WITHDRAWAL_TYPES.DEFAULT)}
                    disabled={values[FIELDS.WITHDRAWAL_TYPE] === WITHDRAWAL_TYPES.DEFAULT}
                    style={{
                      borderRadius: '8px 0px 0px 8px',
                      boxShadow: 'none',
                      height: '42px'
                    }}
                    sx={{
                      width: 1 / 2
                    }}
                  >
                    {translate('organizerWithdrawalTypeDefault')}
                  </Button>
                  <Button
                    variant='contained'
                    fullWidth
                    color='warning'
                    onClick={() => setFieldValue(FIELDS.WITHDRAWAL_TYPE, WITHDRAWAL_TYPES.DAYS)}
                    disabled={values[FIELDS.WITHDRAWAL_TYPE] === WITHDRAWAL_TYPES.DAYS}
                    sx={{
                      width: 1 / 2
                    }}
                    style={{
                      borderRadius: '0px 8px 8px 0px',
                      boxShadow: 'none',
                      height: '42px'
                    }}
                  >
                    {translate('organizerWithdrawalTypeDays')}
                  </Button>
                </Box>
                {values[FIELDS.WITHDRAWAL_TYPE] === WITHDRAWAL_TYPES.DAYS ? (
                  <TextField
                    name={FIELDS.WITHDRAWAL_DAYS}
                    value={values?.[FIELDS.WITHDRAWAL_DAYS]}
                    error={touched?.[FIELDS.WITHDRAWAL_DAYS] && Boolean(errors?.[FIELDS.WITHDRAWAL_DAYS])}
                    helperText={touched?.[FIELDS.WITHDRAWAL_DAYS] && errors?.[FIELDS.WITHDRAWAL_DAYS]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    disabled={isLoading}
                    fullWidth
                    type='number'
                    label={intl.formatMessage({ id: 'organizerWithdrawalDays' })}
                    placeholder={intl.formatMessage({
                      id: 'organizerWithdrawalDays'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                ) : null}
                <Button
                  size='large'
                  variant='contained'
                  color='warning'
                  type='submit'
                  fullWidth
                  disabled={isLoading || !isValid}
                >
                  {translate('organizerUpdateSettings')}
                </Button>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
